import { assign, setup } from 'xstate';
import type { ScheduledBusesEvent } from './events';
import { ROUTES } from './catalogue';

const STORAGE_KEY = 'seattle-bus-times.selection';
const SEATTLE_TIME_ZONE = 'America/Los_Angeles';

type Bus = { time: string };

type ScheduledBusesContext = {
  route: string;
  direction: string;
  stop: string;
  buses: readonly Bus[];
  serviceDay: string;
  checkedAt: string;
  sourceStatus: 'missing' | 'available';
  error: string | null;
};

type StoredSelection = Pick<ScheduledBusesContext, 'route' | 'direction' | 'stop'>;

function isStoredSelection(value: unknown): value is StoredSelection {
  if (typeof value !== 'object' || value === null) return false;
  const selection = value as Record<string, unknown>;
  return (
    typeof selection.route === 'string' && typeof selection.direction === 'string' && typeof selection.stop === 'string'
  );
}

function readSelection(): StoredSelection {
  if (typeof window === 'undefined') return { route: '', direction: '', stop: '' };

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return { route: '', direction: '', stop: '' };
    const parsed: unknown = JSON.parse(stored);
    return isStoredSelection(parsed) ? parsed : { route: '', direction: '', stop: '' };
  } catch {
    return { route: '', direction: '', stop: '' };
  }
}

function saveSelection(selection: StoredSelection): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
  } catch {}
}

function routeFor(id: string) {
  return ROUTES.find((route) => route.id === id);
}

function directionFor(routeId: string, directionId: string) {
  return routeFor(routeId)?.directions.find((direction) => direction.id === directionId);
}

function selectionIsValid(selection: StoredSelection): boolean {
  const direction = directionFor(selection.route, selection.direction);
  return Boolean(direction?.stops.some((stop) => stop.id === selection.stop));
}

function initialContext(): ScheduledBusesContext {
  const stored = readSelection();
  const selection = selectionIsValid(stored) ? stored : { route: '', direction: '', stop: '' };
  return {
    ...selection,
    buses: [],
    serviceDay: '',
    checkedAt: '',
    sourceStatus: 'missing',
    error: null,
  };
}

function seattleServiceDay(now: Date): string {
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone: SEATTLE_TIME_ZONE,
    weekday: 'long',
  }).format(now);

  if (weekday === 'Saturday') return 'saturday';
  if (weekday === 'Sunday') return 'sunday';
  return 'weekday';
}

const scheduledBusesSetup = setup({
  types: {
    context: {} as ScheduledBusesContext,
    events: {} as ScheduledBusesEvent,
  },
  actions: {
    persistSelection: ({ context }) => {
      saveSelection({ route: context.route, direction: context.direction, stop: context.stop });
    },
    checkMissingSchedule: assign({
      buses: [],
      serviceDay: () => seattleServiceDay(new Date()),
      checkedAt: () => new Date().toISOString(),
      sourceStatus: 'missing',
      error: null,
    }),
  },
  guards: {
    isKnownRoute: ({ event }) => event.type === 'scheduledBuses.chooseRoute' && Boolean(routeFor(event.route)),
    isKnownDirection: ({ context, event }) =>
      event.type === 'scheduledBuses.chooseDirection' && Boolean(directionFor(context.route, event.direction)),
    isKnownStop: ({ context, event }) =>
      event.type === 'scheduledBuses.chooseStop' &&
      Boolean(directionFor(context.route, context.direction)?.stops.some((stop) => stop.id === event.stop)),
  },
});

export const scheduledBusesMachine = scheduledBusesSetup.createMachine({
  id: 'scheduledBuses',
  initial: 'idle',
  context: initialContext,
  states: {
    idle: {},
    loading: {
      always: { target: 'ready', actions: 'checkMissingSchedule' },
    },
    ready: {},
    error: {},
  },
  on: {
    'scheduledBuses.chooseRoute': [
      {
        guard: 'isKnownRoute',
        actions: [
          assign(({ event }) => ({ route: event.route, direction: '', stop: '' })),
          assign({ buses: [], error: null }),
          'persistSelection',
        ],
      },
      { actions: 'persistSelection' },
    ],
    'scheduledBuses.chooseDirection': [
      {
        guard: 'isKnownDirection',
        actions: [
          assign(({ event }) => ({ direction: event.direction, stop: '' })),
          assign({ buses: [], error: null }),
          'persistSelection',
        ],
      },
      { actions: 'persistSelection' },
    ],
    'scheduledBuses.chooseStop': [
      {
        guard: 'isKnownStop',
        actions: [assign(({ event }) => ({ stop: event.stop, error: null })), 'persistSelection'],
      },
      { actions: 'persistSelection' },
    ],
    'scheduledBuses.loadSchedule': { target: '.loading' },
    'scheduledBuses.refresh': { target: '.loading' },
  },
});
